import { datadogRum } from '@datadog/browser-rum';
import { v4 as uuidv4 } from 'uuid';
import { localStorage, window } from '../env/browser';
import { API_ENV, DATADOG_SITE } from '../constants';

const CLIENT_SECRET = 'pub7cdc7336b1df5870d03746538cfb8dd1';
const ANONYMOUS_USER_STORAGE_KEY = 'app-shell/tracer/anonymous_id';

function anonymousId(): string {
  const currentAnonymousId = localStorage.getItem(ANONYMOUS_USER_STORAGE_KEY);

  if (!currentAnonymousId) {
    const newId = uuidv4();
    localStorage.setItem(ANONYMOUS_USER_STORAGE_KEY, newId);
    return newId;
  }

  return currentAnonymousId;
}

interface TracerUser {
  merchantId: string;
  employeeId: string;
}

const Tracer = {
  init(): void {
    const { CIRCLE_BUILD_NUM } = process.env;
    const env = API_ENV === 'staging' ? 'staging' : 'prod';

    datadogRum.init({
      env,
      applicationId: '579dc7ba-05b1-4551-b639-358d011e98c3',
      clientToken: CLIENT_SECRET,
      site: DATADOG_SITE,
      service: 'web-calendar-app',
      defaultPrivacyLevel: 'mask-user-input',
      // TODO(tracing): Distributed-tracing header injection is DISABLED as a
      // temporary frontend mitigation. browser-rum v5 (#699) renamed the v4
      // option `allowedTracingOrigins` to `allowedTracingUrls`; CAOP-509 applied
      // that rename, which switched header injection ON for the first time since
      // the v5 upgrade. The auth/identity backend serving `/v2/auth/tokens` then
      // raised `RuntimeError: Request headers not allowed` for the injected
      // headers (traceparent, x-datadog-*), causing intermittent prod errors.
      // Re-enable once the backend accepts these headers (extend its header
      // allowlist + CORS Access-Control-Allow-Headers), then restore:
      //   allowedTracingUrls: [
      //     /https:\/\/.*\.shore\.com/,
      //     /https:\/\/.*\.localsearch\.ch/,
      //   ],
      allowedTracingUrls: [],
      sessionSampleRate: 3,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      version: `${env}-${CIRCLE_BUILD_NUM ?? 'unknown-circle-ci-build-number'}`,
    });

    this.setGlobalContext('anonymous_id', anonymousId());
    this.setGlobalContext('hostname', (window as Window).location.hostname);
  },

  setUser({ merchantId, employeeId }: TracerUser): void {
    datadogRum.removeGlobalContextProperty('anonymous_id');
    datadogRum.setUser({ id: employeeId, merchantId });
  },

  setAppName(name: string): void {
    datadogRum.setGlobalContextProperty('app', name);
  },

  setGlobalContext(contextKey: string, contextValue: unknown): void {
    datadogRum.setGlobalContextProperty(contextKey, contextValue);
  },

  getGlobalContextProperty(name: string): unknown {
    const context = datadogRum.getGlobalContext();
    return context && context[name];
  },

  addAction(name: string, payload: Record<string, unknown> = {}): void {
    datadogRum.addAction(name, payload);
  },

  addError(error: unknown, context?: object): void {
    datadogRum.addError(error, context);
  },

  addTiming(name: string, timing: number): void {
    datadogRum.addTiming(name, timing);
  },

  clearAppConfig(): void {
    datadogRum.removeGlobalContextProperty('app');
  },

  removeUser(): void {
    datadogRum.clearUser();
  },
};

export default Tracer;
